import axios, { AxiosHeaders } from 'axios'

/**
 * Shared axios instance. All API calls go through /api/v1 so requests land on
 * the Go backend via the Vite dev proxy in development and on the same
 * origin in production. `withCredentials` makes the browser send the session
 * cookie set by the login handler.
 */
export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
})

const CSRF_COOKIE = 'csrf_token'
const CSRF_HEADER = 'X-CSRF-Token'
const SAFE_METHODS = new Set(['get', 'head', 'options'])

/**
 * Double-submit CSRF: echo the csrf_token cookie value into X-CSRF-Token
 * for every mutating request. The login request runs before a cookie
 * exists, so the header is simply absent — backend accepts that because
 * /auth/login sits outside the protected group.
 */
api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (SAFE_METHODS.has(method)) {
    return config
  }
  const token = readCookie(CSRF_COOKIE)
  if (!token) {
    return config
  }
  const headers = AxiosHeaders.from(config.headers)
  headers.set(CSRF_HEADER, token)
  config.headers = headers
  return config
})

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const prefix = `${encodeURIComponent(name)}=`
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length))
    }
  }
  return null
}
